import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * The member-readable rollup of their own record.
 *
 * `patient_record_projections/{uid}` is owner-readable with client writes
 * denied, and is maintained by Cloud Functions. It is the only way the member
 * can learn anything about a quarterly report before it is released: the report
 * document itself is unreadable to them until `status == 'released'`, and
 * `care_workflows`, where the status and the delivery call live, is staff-only.
 *
 * Only the fields the member dashboard consumes are typed here. The projection
 * carries more (consent eligibility, cohort and risk tags, observation
 * timestamps) which belongs to the ops and doctors portals.
 */

export type QuarterlyReportCycleStatus =
  | 'drafted'
  | 'care_clinician_review'
  | 'medical_director_signoff'
  | 'released'
  | string;

export interface ReportCycleProjection {
  /** e.g. "q3-2026" — the slug the engine stamped, not a formatted label. */
  quarterLabel: string | null;
  reportStatus: QuarterlyReportCycleStatus | null;
  /**
   * The booked delivery appointment. NOT an estimate — nothing in the schema
   * records an expected release date, and a booked call is a date the practice
   * has actually committed to.
   */
  deliveryCallAt: unknown;
}

export interface PatientRecordProjection {
  userId: string;
  reportCycle: ReportCycleProjection | null;
}

/**
 * Returns null rather than throwing when the projection is missing or
 * unreadable. A member provisioned before the projection existed simply has no
 * document, and the dashboard's own derivation covers that case — so this
 * failing must degrade the quarter stepper, not the page.
 */
export async function getPatientRecordProjection(
  userId: string,
): Promise<PatientRecordProjection | null> {
  const snapshot = await getDoc(doc(db, 'patient_record_projections', userId));
  if (!snapshot.exists()) return null;

  const data = snapshot.data() as Record<string, any>;
  const cycle = data.reportCycle;

  return {
    userId,
    reportCycle: cycle
      ? {
          quarterLabel: cycle.quarterLabel ?? null,
          reportStatus: cycle.reportStatus ?? null,
          deliveryCallAt: cycle.deliveryCallAt ?? null,
        }
      : null,
  };
}
